import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import HashParameterService from '../../../services/hashParameterService.js';
import Gantt from '../Gantt/Gantt.jsx';
import CampaignTable from '../CampaignTable/CampaignTable.jsx';
import ExportButton from '../ExportButton/ExportButton.jsx';
import QueryStringService from '../../../services/queryStringService.js';
import { useFetchCampaignData } from '../../../hooks/campaign/useFetchCampaignData.js';
import { createExporter } from './ExportMappings/exportFactory.js';

import styles from './CampaignOverview.module.css';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const DataFetcher = ({ orderline, customerId, countryCode, onComplete }) => {
    const { data, loading, error } = useFetchCampaignData(
        orderline,
        customerId,
        countryCode
    );

    useEffect(() => {
        let mounted = true;

        if (!loading && mounted) {
            const result = {
                orderlineId: orderline.id,
                data: JSON.parse(JSON.stringify(data)), // Deep clone the data
                error
            };
            onComplete(result);
        }

        return () => {
            mounted = false;
        };
    }, [loading, data, error, orderline.id, onComplete]);

    return null;
};

const CampaignOverview = ({ orderData, customerId, onLoadOrderline, parameters }) => {
    const queryStringService = useMemo(() => new QueryStringService(), []);
    const hashParamService = useMemo(() => new HashParameterService(), []);
    const [hashParamServiceStartDate, setHashParamServiceStartDate] = useState('');
    const [hashParamServiceEndDate, setHashParamServiceEndDate] = useState('');
    const [urlOrderLineIds, setUrlOrderLineIds] = useState([]);
    const [selectedOrderlineId, setSelectedOrderlineId] = useState(-1);
    const [activeOrderline, setActiveOrderline] = useState(null);
    const [fetchedResults, setFetchedResults] = useState(new Map());
    const [resolveExport, setResolveExport] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    const countryCode = useMemo(() =>
        queryStringService.getCurrentCountry(),
        [queryStringService]
    );

    useEffect(() => {
        const hashIds = hashParamService.getParameter('orderlines');
        setHashParamServiceStartDate(hashParamService.getParameter('startDate'));
        setHashParamServiceEndDate(hashParamService.getParameter('endDate'));
        setUrlOrderLineIds(hashIds ? hashIds.split(",") : []);
    }, [hashParamService]);

    const campaignData = useMemo(() => orderData.orderLines.map(o => {
        const startDate = moment(o.periodStart);
        const endDate = moment(o.periodEnd);
        return {
            startYear: startDate.year(),
            startMonth: startDate.month(),
            startDay: startDate.date(),
            durationDays: endDate.diff(startDate, 'days') + 1,
            description: o.name,
            id: o.id
        }
    }), [orderData.orderLines]);

    const handleTableRowClicked = useCallback((orderlineId) => {
        setSelectedOrderlineId(orderlineId);
    }, []);

    const handleCampaignSelect = useCallback((campaign) => {
        setSelectedOrderlineId(campaign.id);
    }, []);

    const handleOpenDeliveryReport = useCallback(() => {
        if (selectedOrderlineId !== -1) {
            onLoadOrderline([selectedOrderlineId]);
        }
    }, [selectedOrderlineId, onLoadOrderline]);

    const handleFetchComplete = useCallback((result) => {
        setFetchedResults(prev => {
            const next = new Map(prev);
            next.set(result.orderlineId, result);
            return next;
        });
    }, []);

    useEffect(() => {
        if (!isExporting || !activeOrderline) return;

        const currentIndex = orderData.orderLines.findIndex(ol => ol.id === activeOrderline.id);

        if (fetchedResults.has(activeOrderline.id)) {
            const nextOrderline = orderData.orderLines[currentIndex + 1];

            if (nextOrderline) {
                setActiveOrderline(nextOrderline);
            } else if (fetchedResults.size === orderData.orderLines.length && resolveExport) {
                const sortedResults = orderData.orderLines
                    .map(ol => fetchedResults.get(ol.id))
                    .filter(Boolean);

                resolveExport(sortedResults);
                setResolveExport(null);
                setIsExporting(false);
                setActiveOrderline(null);
            }
        }
    }, [isExporting, activeOrderline, orderData.orderLines, fetchedResults, resolveExport]);

    const getExportDataByCountry = useCallback((orderline, deliveryData, countryCode, customer, orderId) => {
        const exporter = createExporter(countryCode, orderline, deliveryData, countryCode, customer, orderId);
        return exporter.export(parameters);
    }, []);

    const fetchExportData = useCallback(async () => {
        try {
            setFetchedResults(new Map());
            setIsExporting(true);

            const results = await new Promise(resolve => {
                setResolveExport(() => resolve);
                setActiveOrderline(orderData.orderLines[0]);
            });

            return orderData.orderLines.map((orderline) => {
                const result = results.find(r => r.orderlineId === orderline.id);
                return getExportDataByCountry(
                    orderline,
                    result?.data,
                    countryCode,
                    orderData.customer,
                    orderData.orderId
                );
            });
        } catch (error) {
            console.error('Export failed:', error);
            throw error;
        } finally {
            setActiveOrderline(null);
            setFetchedResults(new Map());
            setResolveExport(null);
            setIsExporting(false);
        }
    }, [orderData, countryCode]);

    return (
        <div className={styles.backgroundContainer}>
            <div className={styles.formContainer}>
                <h2>{orderData.customer}</h2>
                <div>
                    <Gantt
                        expanded={true}
                        months={MONTHS}
                        data={campaignData}
                        onCampaignSelect={handleCampaignSelect}
                        selectedOrderlineId={selectedOrderlineId}
                        onCampaignDoubleClick={handleOpenDeliveryReport}
                    />
                </div>
                <div className={styles.spacingBetweenElements}>
                    <CampaignTable
                        customerId={customerId}
                        orderData={orderData}
                        selectedOrderlineId={selectedOrderlineId}
                        onTableRowClick={handleTableRowClicked}
                        onTableRowDoubleClick={handleOpenDeliveryReport}
                    />
                </div>
                {isExporting && activeOrderline && (
                    <DataFetcher
                        key={activeOrderline.id}
                        orderline={activeOrderline}
                        customerId={customerId}
                        countryCode={countryCode}
                        onComplete={handleFetchComplete}
                    />
                )}
                <div className={`${styles.spacingBetweenElements} ${styles.deliveryButtonContainer}`}>
                    <span>
                        <ExportButton
                            buttonClassName={styles.deliveryButton}
                            textClassName={styles.deliveryButton_text}
                            loadingClassName={styles.deliveryButton_loading}
                            fetchData={fetchExportData}
                            fileNamePrefix={"ProofOfPlay"}
                            sheetName={"campaigns"}
                            exportType="excel"
                        />
                    </span>
                    <span>
                        <button
                            className={styles.deliveryButton}
                            onClick={handleOpenDeliveryReport}
                            disabled={selectedOrderlineId === -1}
                        >
                            Open Delivery Report
                        </button>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CampaignOverview;